import { Injectable } from '@angular/core';
import { Observable } from 'rxjs/Observable';
import { tap, catchError } from 'rxjs/operators';
import { of } from 'rxjs/observable/of';
import 'rxjs/add/operator/toPromise';
import { APP_SETTINGS } from '../../shared/models/backofficeConfiguration';
import { HttpClient, HttpHeaders } from '@angular/common/http';

export const authorizationDataKey: string = "ls.authorizationData";
export const externalAuthDataKey: string = "ls.externalAuthData";
export const modulesDataKey: string = "ls.modulesData";

@Injectable()
export class AuthService {
  httpOptions = {
    headers: new HttpHeaders().append('Content-Type', 'application/json' )
  };
  defaultLanguage: any = { languageId: "7CB7A61D-5D42-46AD-B9C0-FA83CA0D711E", cultureNameISO: "en-US" };

  constructor(private http: HttpClient) { }

  public getToken(): string {
    const authData: any = localStorage.getItem(authorizationDataKey);
    if (authData) {
      let result = JSON.parse(authData).token;
      return result;
    } else {
      this._logOut();
      let redirectUri = location.protocol + '//' + location.host + '/#/login'; // angular 1 login
      window.location.href = redirectUri;
    }
  }

  public modules(): any { return this._modules(); }
  public externalAuthData(): any { return this._externalAuthData; }

  isAuthFromLocalStorage() {
    if (localStorage.getItem(authorizationDataKey)) {
      let authInfo = JSON.parse(localStorage.getItem(authorizationDataKey));
      if (authInfo.token != '') {
        return true;
      }
    }

    return false;
  }

  userRolesFromLocalStorage() {
    if (localStorage.getItem(externalAuthDataKey)) {
      let authData = JSON.parse(localStorage.getItem(externalAuthDataKey));
      return authData.roles;
    }

    return [];
  }

  serviceBase = APP_SETTINGS.AuthorizationApiUrl;
  authServiceFactory: any = {};
  interval = null;

  _authentication = {
    isAuth: false,
    userName: "",
    useRefreshTokens: false,
    tokenExpiration: new Date()
  };

  _externalAuthData = {
    id: "",
    provider: "",
    userName: "",
    externalAccessToken: "",
    avatar: "",
    lastName: "",
    name: "",
    phone: "",
    roles: [],
    languages: [],
    loadSuccessfully: false,
    actualLanguage: {}
  };

  _logOut = function () {
    localStorage.removeItem('authorizationData');
    localStorage.removeItem('externalAuthData');
    localStorage.removeItem('modulesData');

    this._authentication.isAuth = false;
    this._authentication.userName = "";
    this._authentication.useRefreshTokens = false;
    this._authentication.tokenExpiration = new Date();

    this._externalAuthData.provider = "";
    this._externalAuthData.userName = "";
    this._externalAuthData.externalAccessToken = "";
    this._externalAuthData.avatar = "";
    this._externalAuthData.lastName = "";
    this._externalAuthData.name = "";
    this._externalAuthData.phone = "";
    this._externalAuthData.roles = [];
    this._externalAuthData.languages = [];
    this._externalAuthData.loadSuccessfully = false;
    this._externalAuthData.modules = [];
    this._externalAuthData.actualLanguage = {};

  };

  _fillAuthData = function () {

    let storedAuthData: any = localStorage.getItem('authorizationData');
    if (storedAuthData) {
      let authData = JSON.parse(storedAuthData);
      this._authentication.isAuth = true;
      this._authentication.userName = authData.userName;
      this._authentication.useRefreshTokens = authData.useRefreshTokens;
      this._authentication.tokenExpiration = new Date(authData.tokenExpiration);
    }

    let storedExternalData: any = localStorage.getItem('externalAuthData');
    if (storedExternalData) {
      let externalData = JSON.parse(storedExternalData);

      this._externalAuthData.id = externalData.id;
      this._externalAuthData.provider = externalData.provider;
      this._externalAuthData.userName = externalData.userName;
      this._externalAuthData.externalAccessToken = externalData.externalAccessToken;
      this._externalAuthData.avatar = externalData.avatar;
      this._externalAuthData.lastName = externalData.lastName;
      this._externalAuthData.name = externalData.name;
      this._externalAuthData.phone = externalData.phone;
      this._externalAuthData.roles = externalData.roles;
      this._externalAuthData.languages = externalData.languages;
      this._externalAuthData.loadSuccessfully = true;
      this._externalAuthData.actualLanguage = externalData.actualLanguage;
    }

  };

  _modules = function () {
    var modules = [];
    var moduleData: any = localStorage.getItem(modulesDataKey);
    if (moduleData) {
      let result = JSON.parse(moduleData);
      modules = result.modules;
    }
    return modules;
  };

  _obtainAccessToken = function (externalData) {

    let promise = new Promise((resolve, reject) => {
      this.http.get(this.serviceBase + 'account/ObtainLocalAccessToken', { params: { provider: externalData.provider, externalAccessToken: externalData.externalAccessToken } })
        .toPromise()
        .success(function (response) {

          localStorage.setItem('authorizationData',
            JSON.stringify({
              token: response.token.access_token,
              userName: response.token.userName,
              refreshToken: "",
              useRefreshTokens: false,
              tokenExpiration: response.token['.expires']
            }));

          this._authentication.isAuth = true;
          this._authentication.userName = response.token.userName;
          this._authentication.useRefreshTokens = false;
          this._authentication.tokenExpiration = new Date(response.token['.expires']);

          localStorage.setItem('externalAuthData',
            JSON.stringify({
              id: response.user.id,
              name: response.user.name,
              lastName: response.user.lastName,
              avatar: response.user.avatar,
              userName: response.user.userName,
              roles: response.user.roles,
              languages: response.user.languages
            }));

          this._externalAuthData.id = response.user.id;
          this._externalAuthData.name = response.user.name;
          this._externalAuthData.lastName = response.user.lastName;
          this._externalAuthData.avatar = response.user.avatar;
          this._externalAuthData.userName = response.user.userName;
          this._externalAuthData.roles = response.user.roles;
          this._externalAuthData.languages = response.user.languages;

          resolve(response);
        }).error(function (err, status) {
          this._logOut();
          reject(err);
        });
    });

    return promise;
  };

  _registerExternal(registerExternalData: any): Observable<any> {
    return this.http.post<any>(this.serviceBase + 'account/registerexternal', registerExternalData, this.httpOptions).
      pipe(
        tap((registerExternalData: any) => console.info(`external data registered `)),
        catchError(
          // this._logOut();
          this.handleError<any>('registerExternal'))
      );
  };

  private handleError<T>(operation = 'operation', result?: T) {
    this._logOut();
    return (error: any): Observable<T> => {
      console.error(error);
      console.info(`${operation} failed: ${error.message}`);
      // Let the app keep running by returning an empty result.
      return of(result as T);
    };
  };

  _loadExternalAuthData() {
    let url: string = this.serviceBase + "account/" + this._externalAuthData.provider + "/invitation?accessToken=" + this._externalAuthData.externalAccessToken;
    return this.http.get<object[]>(url).
      pipe(
        tap(_ => console.info(`loadExternalAuthData`)),
        catchError(this.handleError<object[]>('Error loadExternalAuthData', []))
      ).subscribe(data => { this.fillExternalAuthData(data) })
  };

  private fillExternalAuthData(data: any): void {
    localStorage.setItem('externalAuthData',
      JSON.stringify({
        name: data.name,
        lastName: data.lastName,
        avatar: data.avatar,
        userName: data.userName,
        roles: data.roles,
        languages: data.languages
      }));

    this._externalAuthData.name = data.name;
    this._externalAuthData.lastName = data.lastName;
    this._externalAuthData.phone = data.phone;
    this._externalAuthData.roles = data.roles;
    this._externalAuthData.languages = data.languages;
    this._externalAuthData.loadSuccessfully = true;
    this._externalAuthData.userName = data.userName;
  };

  _loadModule = function () {
    let promise = new Promise((resolve, reject) => {
      this.http.get(this.serviceBase + "dashboard/Module").toPromise()
        .success(function (response) {
          localStorage.setItem('modulesData', JSON.stringify({ modules: response }));
          resolve(response);
        }).error(function (err, status) {
          this._logOut();
          reject(err);
        });
    });

    return promise;
  };

  _isAllowedCultureName = function (cultureName) {
    if (cultureName === undefined || cultureName === null || cultureName === "")
      return false;
    var langs = this._externalAuthData.language.filter(function (lang, index) {
      return (lang.cultureNameISO === cultureName);
    });
    var result = langs.length > 0;
    return result;
  };

  _hasSecurityRoles = function (requiredRoles) {
    var hasRole = false,
      roleCheckPassed = false,
      loweredRoles;
    if (!this._authentication || !this._authentication.isAuth) {
      hasRole = false;
    } else if (this._authentication.isAuth && !requiredRoles) {
      hasRole = true;
    } else if (this._authentication.isAuth && requiredRoles) {
      if (requiredRoles.length === 0) {
        hasRole = true;
      } else {
        loweredRoles = [];
        this._externalAuthData.roles.foreach(role => {
          loweredRoles.push(role.toLowerCase());
        });

        // check user has at least one role in given required roles
        requiredRoles.foreach(role => {
          roleCheckPassed = roleCheckPassed || !!loweredRoles.findFirst(function (i) {
            return i === role.toLowerCase();
          });
        });

        hasRole = roleCheckPassed;
      }
    }

    return hasRole;
  };

  getUserId = function () {
    if (localStorage.getItem(externalAuthDataKey)) {
      let authData = JSON.parse(localStorage.getItem(externalAuthDataKey));
      return authData.id;
    }
    return undefined;
  };

  _isAdmin(roles: string[]): boolean {
    //var isAdmin = $.inArray("Admin", roles) !== -1;
    var isAdmin = roles.find(x => x === 'Admin');
    return (isAdmin != undefined);
  };


  _getTokenExpiration = function () {
    return this._authentication.tokenExpiration;
  };

  _checkInitialLogOut = function () {
    if (this.getMinutesToSessionExpire() < 30) {
      this._logOut();
    }
  };

  _sessionExpiresReminder = function (callback) {
    var fiveMinutesInterval = 300000;

    var interval = setInterval(function () {
      var isLessThanHalfHour = this.isLessThanHalfHourToExpireSession();
      if (isLessThanHalfHour) {
        callback();
        clearInterval(interval);
      }
    }, fiveMinutesInterval);
  };

  setLanguage = function (language) {
    let externalData = localStorage.getItem(externalAuthDataKey);
    this._externalAuthData = JSON.parse(externalData);
    this._externalAuthData.actualLanguage = language;
    localStorage.setItem(externalAuthDataKey, JSON.stringify(this._externalAuthData));
  };

  getActualLanguage = function (): any {
    let externalDataStorage: any;
    externalDataStorage = JSON.parse(localStorage.getItem(externalAuthDataKey));
    if (externalDataStorage != null && externalDataStorage.actualLanguage != null && externalDataStorage.actualLanguage.cultureNameISO != null) {
      return externalDataStorage.actualLanguage;
    }

    return this.defaultLanguage;
  };

  getUserLanguages = function (): any[] {
    let externalDataStorage: any;
    externalDataStorage = JSON.parse(localStorage.getItem(externalAuthDataKey));
    if (externalDataStorage) {
      return externalDataStorage.languages;
    }

    return [];
  };

  getMinutesToSessionExpire = function () {
    var result = -1;
    if (!this._authentication.isAuth) {
      return result;
    }

    var tokenExpiration = this._getTokenExpiration();
    if (!tokenExpiration) {
      return result;
    }

    var diff = tokenExpiration - Date.now();
    var minutes = Math.floor((diff / 1000) / 60);
    return minutes;
  };

  isLessThanHalfHourToExpireSession = function () {
    var minutes = this.getMinutesToSessionExpire();
    var result = (minutes <= 30);
    return result;
  };

}



// WEBPACK FOOTER //
// ./src/app/authentication/services/auth.service.ts